<template>
  <v-container fluid>
    <v-row align="center" justify="center" no-gutters>
      <v-col cols="12">
        <v-toolbar color="white" flat>
          <v-toolbar-title class="text-h5">应用版本</v-toolbar-title>
        </v-toolbar>
      </v-col>

      <v-col cols="12">
        <v-card outlined>
          <v-card-actions class="pb-0 px-4">
            <v-subheader class="px-0">应用版本列表</v-subheader>
            <v-spacer></v-spacer>
            <v-btn v-if="!newPanel" outlined small color="primary" @click.stop="newPanel = true">新增</v-btn>
          </v-card-actions>

          <v-expand-transition>
            <upload-app v-show="newPanel" @close="newPanel = false" @alert="showAlert" @add="addItem"/>
          </v-expand-transition>

          <v-card-text>
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="appListDisplay"
              :search="search"
              :items-per-page="20"
              :footer-props="{itemsPerPageOptions: [10,20,50,100]}"
            >
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-select style="max-width: 200px" :items="osSelect" label="系统" v-model="selectedOS"/>
                  <v-text-field class="ml-5" v-model="search" append-icon="mdi-magnify" label="搜索版本"/>
                </v-toolbar>
              </template>

              <template v-slot:item.os="{ item }">
                <v-icon>{{ item.os | osIconFilter }}</v-icon>
                {{ item.os | osFilter }}
              </template>

              <template v-slot:item.valid="{ item }">
                <v-chip :color="item.valid ? 'green' : 'red'" dark @click.stop="changeStatus(item)">
                  {{ item.valid | validFilter }}
                </v-chip>
              </template>

              <template v-slot:item.createTime="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ processDate(item.createTime).distance }}
                    </span>
                  </template>
                  <span>{{ processDate(item.createTime).time }}</span>
                </v-tooltip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small class="mr-2" @click.stop="download(item.filePath)">mdi-file-download</v-icon>
                    </span>
                  </template>
                  <span>下载</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small class="mr-2" @click.stop="editApp(item)">mdi-pencil</v-icon>
                    </span>
                  </template>
                  <span>修改版本信息</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small @click.stop="editLink(item)">mdi-link-variant</v-icon>
                    </span>
                  </template>
                  <span>分配学校</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog.open" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">修改版本信息</span>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-select
                  :items="osList"
                  item-text="name"
                  item-value="value"
                  v-model="editDialog.item.os"
                  label="操作系统"
                  disabled
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="版本号"
                  dense
                  v-model="editDialog.item.versionName"
                  :disabled="editDialog.item.os === 'ANDROID'"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="下载地址"
                  dense
                  v-model="editDialog.item.filePath"
                  :disabled="editDialog.item.os === 'ANDROID'"
                />
              </v-col>
              <v-col cols="12" v-show="editDialog.item.os === 'ANDROID'">
                <v-text-field
                  label="SHA-512"
                  dense
                  disabled
                  v-model="editDialog.item.sha512"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="最小可用版本"
                  dense
                  v-model="editDialog.item.minVersion"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  label="版本详情"
                  rows="3"
                  dense
                  v-model="editDialog.item.detail"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="editDialog.open = false">关闭</v-btn>
          <v-btn
            text
            color="blue darken-1"
            :loading="editDialog.loading"
            :disabled="editDialog.loading"
            @click="saveChange"
          >
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="linkDialog.open" fullscreen hide-overlay transition="dialog-bottom-transition">
      <app-link-dialog
        @alert="showAlert"
        @close="linkDialog.open = false"
        :app-info="linkDialog.item"
        :customers="customerList"
        :status="linkDialog.open"/>
    </v-dialog>
  </v-container>
</template>

<script>
import UploadApp from '@/components/UploadApp'
import { buildAxios } from '@/plugins/axios'
import { handleResponse } from '@/plugins/errorHandler'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import zhCN from 'date-fns/locale/zh-CN'
import AppLinkDialog from '@/components/AppLinkDialog'

export default {
  name: 'AppInfo',
  components: {
    AppLinkDialog,
    UploadApp
  },
  data () {
    return {
      newPanel: false,
      osSelect: [],
      selectedOS: '全部',
      loading: true,
      search: '',
      headers: [
        {
          text: 'ID',
          value: 'id',
          filterable: false
        },
        {
          text: '系统',
          value: 'os',
          filterable: false
        },
        {
          text: '版本',
          value: 'versionName'
        },
        {
          text: '最小版本',
          value: 'minVersion'
        },
        {
          text: '是否生效',
          value: 'valid',
          filterable: false
        },
        {
          text: '创建时间',
          value: 'createTime',
          filterable: false
        },
        {
          text: '操作',
          value: 'actions',
          sortable: false,
          filterable: false
        }
      ],
      appList: [],
      editDialog: {
        open: false,
        loading: false,
        item: {}
      },
      osList: [
        {
          name: 'Android',
          value: 'ANDROID'
        },
        {
          name: 'iOS',
          value: 'IOS'
        },
        {
          name: '其它',
          value: 'OTHER'
        }
      ],
      linkDialog: {
        open: false,
        item: {}
      },
      customerList: []
    }
  },
  computed: {
    appListDisplay () {
      if (Object.values(osList).includes(this.selectedOS)) {
        return this.appList.filter(a => osList[a.os] === this.selectedOS)
      } else {
        return this.appList
      }
    }
  },
  created () {
    this.init()
    this.loadCustomers()
    this.osSelect = Object.values(osList)
    this.osSelect.unshift('全部')
  },
  methods: {
    showAlert (arg) {
      this.$emit('alert', arg)
    },
    init () {
      const request = buildAxios()
      request.get('app/getAll')
        .then(res => {
          this.appList = res.data.data
          this.loading = false
        })
        .catch(reason => {
          this.loading = false
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    loadCustomers () {
      const request = buildAxios()
      request.get('uni/infoList')
        .then(res => {
          let cl = res.data.data
          cl = cl.filter(c => !!c.extraInfo)
          this.customerList = cl.sort((a, b) => {
            if (a.extraInfo.pinyin > b.extraInfo.pinyin) {
              return 1
            } else if (a.extraInfo.pinyin < b.extraInfo.pinyin) {
              return -1
            } else {
              return 0
            }
          })
        })
        .catch(reason => {
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    processDate (date) {
      const time = parseISO(date)
      return {
        distance: formatDistanceToNow(time, { locale: zhCN }) + '前',
        time: format(time, 'yyyy-MM-dd HH:mm:ss')
      }
    },
    changeStatus (item) {
      console.log(item)
      const request = buildAxios()
      request.post('app/modify', {
        id: item.id,
        valid: !item.valid
      })
        .then(res => {
          const chApp = res.data.data
          const idx = this.appList.findIndex(a => a.id === chApp.id)
          this.$set(this.appList, idx, chApp)

          this.$emit('alert', {
            msg: chApp.valid ? '已生效' : '已失效',
            type: 'success'
          })
        })
        .catch(reason => {
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    addItem (item) {
      this.appList.unshift(item)
      this.$emit('alert', {
        msg: '应用版本添加成功',
        type: 'success'
      })
    },
    editApp (item) {
      this.editDialog.item = Object.assign({}, item)
      this.editDialog.open = true
      this.editDialog.loading = false
    },
    saveChange () {
      this.editDialog.loading = true
      const payload = {
        id: this.editDialog.item.id,
        minVersion: this.editDialog.item.minVersion,
        detail: this.editDialog.item.detail
      }
      if (this.editDialog.item.os !== 'ANDROID') {
        payload.versionName = this.editDialog.item.versionName
        payload.filePath = this.editDialog.item.filePath
      }

      const request = buildAxios()
      request.post('app/modify', payload)
        .then(res => {
          this.editDialog.loading = false
          this.editDialog.open = false

          const chApp = res.data.data
          const idx = this.appList.findIndex(a => a.id === chApp.id)
          this.$set(this.appList, idx, chApp)

          this.$emit('alert', {
            msg: '修改成功',
            type: 'success'
          })
        })
        .catch(reason => {
          this.editDialog.loading = false
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    editLink (item) {
      this.linkDialog.item = item
      this.linkDialog.open = true
    },
    download (path) {
      window.open(path, '_blank')
    }
  },
  filters: {
    osFilter: value => {
      return osList[value]
    },
    osIconFilter: value => {
      return osIcon[value]
    },
    validFilter: value => {
      return value ? '已生效' : '未生效'
    }
  }
}

const osList = {
  IOS: 'iOS',
  ANDROID: 'Android',
  OTHER: '其它'
}
const osIcon = {
  IOS: 'mdi-apple',
  ANDROID: 'mdi-android',
  OTHER: 'mdi-chip'
}
</script>
